import React, { useState, useEffect, useRef } from 'react';
import { createExerciseRequest, findSimilarExercises } from '../lib/database';
import { useScrollLock } from '../hooks/useScrollLock';
import { useSheetDrag } from '../hooks/useSheetDrag';

const MUSCLE_GROUPS = [
  'Petto', 'Schiena', 'Spalle', 'Bicipiti', 'Tricipiti',
  'Quadricipiti', 'Femorali', 'Glutei', 'Polpacci', 'Core',
  'Avambracci', 'Trapezio', 'Cardio', 'Mobilità'
];

/**
 * Bottom sheet per proporre un nuovo esercizio.
 * @param {string} initialName - Nome pre-compilato dalla ricerca
 * @param {string} userId
 * @param {string} userRole
 * @param {boolean} allowAddToSession - Se true mostra il bottone "Proponi + aggiungi"
 * @param {function} onClose
 * @param {function} onProposed - Chiamata con {template, justProposal} dopo invio
 *   template è null se justProposal=true, altrimenti è il pending template
 */
function ProposeExerciseSheet({ initialName = '', userId, userRole, allowAddToSession = true, onClose, onProposed }) {
  useScrollLock(true);
  const { contentRef, sheetStyle, overlayOpacity, handleTouchStart, handleTouchMove, handleTouchEnd, handleClose } = useSheetDrag(onClose);

  const [name, setName] = useState(initialName);
  const [muscleGroup, setMuscleGroup] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [similarExercises, setSimilarExercises] = useState([]);
  const debounceRef = useRef(null);

  // Check for duplicates with a 600ms debounce after the user stops typing
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (name.trim().length < 3) { setSimilarExercises([]); return; }
    debounceRef.current = setTimeout(async () => {
      const similar = await findSimilarExercises(name.trim());
      setSimilarExercises(similar);
    }, 600);
    return () => clearTimeout(debounceRef.current);
  }, [name]);

  const handleSubmit = async (addToSession) => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const { request, template } = await createExerciseRequest({
        name: name.trim(),
        suggestedMuscleGroup: muscleGroup || null,
        note: note.trim() || null,
        userId,
        userRole,
        createPendingTemplate: addToSession
      });

      if (addToSession && template) {
        onProposed({ template, justProposal: false });
      } else {
        setDone(true);
      }
    } catch (err) {
      console.error('Errore proposta esercizio:', err);
      setSubmitError(err.message || 'Errore durante l\'invio. Riprova.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] animate-fade-in"
      style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }}
      onClick={handleClose}
    >
      <div
        ref={contentRef}
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl"
        style={{ ...sheetStyle, maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        <div className="flex items-center justify-between px-6 pb-4">
          <h2 className="text-xl font-bold text-gray-900">Proponi un esercizio</h2>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full text-gray-600 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="px-6 pb-8 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
          {done ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">✅</div>
              <p className="text-lg font-semibold text-gray-900 mb-2">Richiesta inviata!</p>
              <p className="text-sm text-gray-500">L'esercizio sarà valutato dallo staff e aggiunto al catalogo.</p>
              <button
                onClick={onClose}
                className="mt-6 px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold"
              >
                Chiudi
              </button>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome esercizio *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setSubmitError(null); }}
                  placeholder="Es. Romanian Deadlift"
                  required
                  autoFocus
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {similarExercises.length > 0 && (
                  <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                    <span className="font-semibold">Esercizi simili già presenti:</span>{' '}
                    {similarExercises.map(e => e.name).join(', ')}. Verifica che non sia già nel catalogo.
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Gruppo muscolare <span className="font-normal text-gray-400">(opzionale)</span>
                </label>
                <select
                  value={muscleGroup}
                  onChange={(e) => setMuscleGroup(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                >
                  <option value="">Seleziona...</option>
                  {MUSCLE_GROUPS.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Note <span className="font-normal text-gray-400">(opzionale)</span>
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Descrivi brevemente l'esercizio o perché vuoi aggiungerlo..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
              </div>

              <p className="text-xs text-gray-400">
                La richiesta sarà revisionata dallo staff prima di essere aggiunta al catalogo ufficiale.
              </p>

              {submitError && (
                <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                  ⚠️ {submitError}
                </div>
              )}

              <div className="space-y-3 pt-2">
                {allowAddToSession && (
                  <button
                    type="button"
                    disabled={!name.trim() || submitting}
                    onClick={() => handleSubmit(true)}
                    className="w-full py-3 px-6 bg-primary-600 text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Invio...' : 'Proponi + aggiungi alla sessione'}
                  </button>
                )}
                <button
                  type="button"
                  disabled={!name.trim() || submitting}
                  onClick={() => handleSubmit(false)}
                  className="w-full py-3 px-6 bg-gray-100 text-gray-700 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Invio...' : 'Solo proposta'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProposeExerciseSheet;
